import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
  Typography,
  Divider,
  Fade,
  LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WidgetsIcon from '@mui/icons-material/Widgets';
import { useTranslation } from 'react-i18next';
import { useWebSocket, useUser } from './WebSocketContext';
import AddWidgetModal from './Widgets/AddWidgetModal';

const primaryColor = '#5757b0';
const hoverColor = '#7777d0';
const MAX_WIDGET_POLL_ATTEMPTS = 8;
const WIDGET_POLL_SLOW_INTERVAL_MS = 4000;
const WIDGET_POLL_INTERVAL_MS = 1500;

const WidgetPreviewStep = ({ caseid, onContinue }) => {
  const { t } = useTranslation();
  const { sendMessage, subscribeToMessages, lastCaseCreationUpdate, lastCaseCreationUpdateAt } = useWebSocket();
  const { isReadOnly } = useUser();
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [hadWidgets, setHadWidgets] = useState(false);
  const [pendingWidgets, setPendingWidgets] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [widgetModalOpen, setWidgetModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [widgetToDelete, setWidgetToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [creationUpdate, setCreationUpdate] = useState('');
  const [lastCreationUpdateAt, setLastCreationUpdateAt] = useState(null);

  const requestWidgets = useCallback(() => {
    if (!sendMessage || !caseid) return;
    sendMessage({ command: 'getWidgets', caseid });
  }, [sendMessage, caseid]);

  useEffect(() => {
    setWidgets([]);
    setLoading(true);
    setPollAttempts(0);
    setHasError(false);
    setHadWidgets(false);
    setPendingWidgets(false);
    setCreationUpdate('');
    setLastCreationUpdateAt(null);
  }, [caseid]);

  useEffect(() => {
    requestWidgets();
  }, [requestWidgets]);

  useEffect(() => {
    const handleMessage = (message) => {
      if (message.command === 'getWidgets') {
        const payload = message.data || {};
        if (payload.status === 'pending') {
          setPendingWidgets(true);
          setLoading(false);
          setWidgets([]);
          return;
        }
        if (payload.status === 'error') {
          setHasError(true);
          setLoading(false);
          setWidgets([]);
          return;
        }

        const widgetsData = Array.isArray(payload)
          ? payload
          : Array.isArray(payload.widgets)
            ? payload.widgets
            : [];

        setWidgets(widgetsData);
        if (widgetsData.length > 0) {
          setHadWidgets(true);
        }
        setPendingWidgets(false);
        setLoading(false);
        setHasError(false);
        return;
      }

      if (message.command === 'deleteWidget') {
        setDeleteLoading(false);
        setDeleteDialogOpen(false);
        setWidgetToDelete(null);
        requestWidgets();
        return;
      }

      if (message.command === 'addWidget' || message.command === 'updateWidget') {
        requestWidgets();
        return;
      }

      if (message.command === 'UpdateCreationCase') {
        const updateText = message?.data?.message;
        if (updateText) {
          setCreationUpdate(updateText);
          setLastCreationUpdateAt(Date.now());
        }
      }
    };

    const unsubscribe = subscribeToMessages(
      ['getWidgets', 'addWidget', 'updateWidget', 'deleteWidget', 'UpdateCreationCase'],
      handleMessage
    );

    return unsubscribe;
  }, [subscribeToMessages, requestWidgets]);

  useEffect(() => {
    if (loading || hasError) return;
    if (!pendingWidgets && pollAttempts >= MAX_WIDGET_POLL_ATTEMPTS) return;

    const delay = pendingWidgets
      ? WIDGET_POLL_SLOW_INTERVAL_MS
      : (widgets.length > 0 ? WIDGET_POLL_SLOW_INTERVAL_MS : WIDGET_POLL_INTERVAL_MS);

    const timer = setTimeout(() => {
      if (!pendingWidgets) {
        setPollAttempts((prev) => prev + 1);
      }
      requestWidgets();
    }, delay);

    return () => clearTimeout(timer);
  }, [loading, widgets.length, hasError, pollAttempts, pendingWidgets, requestWidgets]);

  useEffect(() => {
    if (!lastCaseCreationUpdate) return;
    if (!lastCaseCreationUpdateAt) {
      setCreationUpdate(lastCaseCreationUpdate);
      requestWidgets();
      return;
    }
    if (!lastCreationUpdateAt || lastCaseCreationUpdateAt > lastCreationUpdateAt) {
      setCreationUpdate(lastCaseCreationUpdate);
      setLastCreationUpdateAt(lastCaseCreationUpdateAt);
      requestWidgets();
    }
  }, [lastCaseCreationUpdate, lastCaseCreationUpdateAt, lastCreationUpdateAt, requestWidgets]);


  const hasWidgets = widgets.length > 0;
  const showLoadingState = loading || pendingWidgets;
  const showNoWidgets = !showLoadingState && !hasWidgets && !hasError && hadWidgets;
  const isAwaitingWidgets = !showLoadingState && !hasWidgets && !hasError && !hadWidgets;
  const pollComplete = pollAttempts >= MAX_WIDGET_POLL_ATTEMPTS;
  const isGeneratingWidgets = pendingWidgets || (!hasError && !showNoWidgets && !hasWidgets);

  const emptyStateTitle = useMemo(() => {
    if (hasError) return t('widget_preview.error_title', 'Unable to load widgets');
    if (showNoWidgets) return t('widget_preview.empty_title', 'No widgets');
    if (isAwaitingWidgets && creationUpdate && !/widget/i.test(creationUpdate)) {
      return t('widget_preview.preparing_title', 'Preparing your project...');
    }
    return t('widget_preview.generating_title', 'Generating widgets...');
  }, [hasError, showNoWidgets, isAwaitingWidgets, creationUpdate, t]);

  const emptyStateBody = useMemo(() => {
    if (hasError) return t('widget_preview.error_body', 'We could not load widgets. Please try again later.');
    if (showNoWidgets) {
      return t('widget_preview.empty_body', 'You removed all widgets. You can add one later or continue.');
    }
    if (isAwaitingWidgets && creationUpdate) {
      return creationUpdate;
    }
    if (isAwaitingWidgets && !creationUpdate) {
      return t('widget_preview.generating_body', 'This can take a moment. Please wait.');
    }
    if (isAwaitingWidgets && creationUpdate && !/widget/i.test(creationUpdate)) {
      return t('widget_preview.preparing_body', 'Setting up your project. Widgets are coming next.');
    }
    return t('widget_preview.generating_body', 'This can take a moment. Please wait.');
  }, [hasError, showNoWidgets, isAwaitingWidgets, creationUpdate, t]);

  const gridTemplateColumns = useMemo(() => ({ xs: '1fr', md: widgets.length === 1 ? 'minmax(320px, 680px)' : '1fr 1fr' }), [widgets.length]);

  const handleAddWidget = () => {
    setEditingWidget(null);
    setWidgetModalOpen(true);
  };

  const handleEditWidget = (widget) => {
    setEditingWidget(widget);
    setWidgetModalOpen(true);
  };

  const handleCloseModal = () => {
    setWidgetModalOpen(false);
    setEditingWidget(null);
  };

  const handleRequestDelete = (widget) => {
    setWidgetToDelete(widget);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setWidgetToDelete(null);
    setDeleteLoading(false);
  };

  const handleDeleteConfirm = () => {
    if (!sendMessage || !widgetToDelete?.id) {
      handleDeleteCancel();
      return;
    }
    setDeleteLoading(true);
    sendMessage({
      command: 'deleteWidget',
      widget_id: widgetToDelete.id
    });
  };

  return (
    <Box sx={{ width: '100%', px: { xs: 1, sm: 2 }, py: { xs: 2, sm: 3 } }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <WidgetsIcon sx={{ fontSize: 44, color: primaryColor, mb: 1 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: primaryColor, mb: 1 }}>
          {t('widget_preview.title', 'Review AI widgets')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 620, mx: 'auto' }}>
          {t(
            'widget_preview.subtitle',
            'We created these widgets from your goal. Edit anything you want, then continue to data sources.'
          )}
        </Typography>
        {!isReadOnly && hasWidgets && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={handleAddWidget}
              startIcon={<AddIcon />}
              sx={{
                borderColor: 'rgba(87, 87, 176, 0.35)',
                color: primaryColor,
                textTransform: 'none',
                px: 3,
                width: { xs: '100%', sm: 'auto' },
                maxWidth: { xs: 320, sm: 'none' },
                '&:hover': {
                  borderColor: primaryColor,
                  backgroundColor: 'rgba(87, 87, 176, 0.08)'
                }
              }}
            >
              {t('widget_preview.add', 'Add widget')}
            </Button>
          </Box>
        )}
      </Box>

      {showLoadingState && (
        <Fade in timeout={300}>
          <Box>
            <Box
              sx={{
                maxWidth: 520,
                mx: 'auto',
                mb: 2.5,
                p: 2,
                borderRadius: 3,
                border: '1px solid rgba(87, 87, 176, 0.12)',
                background: 'linear-gradient(180deg, rgba(87,87,176,0.08) 0%, rgba(255,255,255,0.9) 100%)',
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.55) 35%, transparent 70%)',
                  transform: 'translateX(-120%)',
                  animation: 'shimmerSweep 2.2s ease-in-out infinite'
                },
                '@keyframes shimmerSweep': {
                  '0%': { transform: 'translateX(-120%)' },
                  '60%': { transform: 'translateX(120%)' },
                  '100%': { transform: 'translateX(120%)' }
                }
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: primaryColor }}>
                {t('widget_preview.wait_title', 'Building your widgets')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('widget_preview.wait_body', 'This may take a few seconds.')}
                </Typography>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    gap: 0.5,
                    '& span': {
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: primaryColor,
                      display: 'inline-block',
                      animation: 'pulseDot 1.2s ease-in-out infinite'
                    },
                    '& span:nth-of-type(2)': { animationDelay: '0.15s' },
                    '& span:nth-of-type(3)': { animationDelay: '0.3s' },
                    '@keyframes pulseDot': {
                      '0%, 100%': { transform: 'scale(0.7)', opacity: 0.4 },
                      '50%': { transform: 'scale(1)', opacity: 1 }
                    }
                  }}
                >
                  <span />
                  <span />
                  <span />
                </Box>
              </Box>
              <LinearProgress
                sx={{
                  mt: 1.5,
                  height: 6,
                  borderRadius: 999,
                  backgroundColor: 'rgba(87, 87, 176, 0.12)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: primaryColor,
                    borderRadius: 999
                  }
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: gridTemplateColumns,
                justifyContent: widgets.length === 1 ? 'center' : 'stretch',
                justifyItems: widgets.length === 1 ? 'center' : 'stretch',
                alignItems: 'stretch',
                gap: 2
              }}
            >
              {[0, 1, 2].map((item) => (
                <Card
                  key={item}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: { xs: 160, sm: 200 },
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid rgba(87, 87, 176, 0.12)',
                    boxShadow: '0 10px 24px rgba(87, 87, 176, 0.08)',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.45) 40%, transparent 80%)',
                      transform: 'translateX(-120%)',
                      animation: 'shimmerSweep 2.4s ease-in-out infinite',
                      pointerEvents: 'none'
                    }
                  }}
                >
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, p: 2.5 }}>
                    <Skeleton width="60%" height={28} />
                    <Skeleton width="40%" height={20} sx={{ mt: 0.5 }} />
                    <Skeleton variant="rectangular" height={64} sx={{ mt: 1.5, borderRadius: 1.5 }} />
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        </Fade>
      )}

      {!showLoadingState && hasWidgets && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: gridTemplateColumns,
            justifyContent: widgets.length === 1 ? 'center' : 'stretch',
            justifyItems: widgets.length === 1 ? 'center' : 'stretch',
            alignItems: 'stretch',
            gap: 2
          }}
        >
          {widgets.map((widget) => (
            <Card
              key={widget.id || widget.variable || widget.title}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: { xs: 160, sm: 200 },
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid rgba(87, 87, 176, 0.08)',
                boxShadow: '0 8px 32px rgba(87, 87, 176, 0.12), 0 2px 8px rgba(87, 87, 176, 0.08)',
                background: 'linear-gradient(180deg, rgba(248,250,252,0.6) 0%, #ffffff 100%)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                ...(widgets.length === 1 ? { maxWidth: 680, mx: 'auto' } : {}),
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 14px 40px rgba(87, 87, 176, 0.14)'
                }
              }}
            >
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, justifyContent: 'space-between', p: 2.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'flex-start',
                    gap: { xs: 1, sm: 2 },
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <WidgetsIcon sx={{ mr: 1, fontSize: '1.1rem', color: primaryColor }} />
                      <Typography
                        variant="h6"
                        sx={{
                          color: primaryColor,
                          fontWeight: 600,
                          fontSize: '1.05rem',
                          overflowWrap: 'anywhere',
                          letterSpacing: 0.2,
                          textShadow: '0 1px 2px rgba(0,0,0,0.06)',
                          minWidth: 0,
                          flex: '0 1 auto'
                        }}
                      >
                        {widget.title || t('widget_preview.untitled', 'Untitled widget')}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1, borderColor: 'rgba(87, 87, 176, 0.06)' }} />
                  </Box>
                  {!isReadOnly && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'row', sm: 'row' },
                        gap: 1,
                        alignItems: 'center',
                      }}
                    >
                      <Tooltip title={t('widget_preview.edit', 'Edit')}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditWidget(widget)}
                          aria-label={t('widget_preview.edit', 'Edit')}
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: primaryColor,
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: 'rgba(87, 87, 176, 0.06)',
                              transform: 'scale(1.06)'
                            },
                            transition: 'all 0.18s ease'
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('widget_preview.delete', 'Delete')}>
                        <IconButton
                          size="small"
                          onClick={() => handleRequestDelete(widget)}
                          aria-label={t('widget_preview.delete', 'Delete')}
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#c62828',
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: 'rgba(211, 47, 47, 0.06)',
                              transform: 'scale(1.06)'
                            },
                            transition: 'all 0.18s ease'
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    whiteSpace: 'normal',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {widget.description || t('widget_preview.no_description', 'No description provided.')}
                </Typography>
                {Array.isArray(widget.variables) && widget.variables.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {widget.variables.map((variable) => (
                      <Chip
                        key={variable}
                        label={variable}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(87, 87, 176, 0.08)',
                          color: primaryColor,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          borderRadius: '6px',
                          height: '22px',
                          alignSelf: 'flex-start',
                          '& .MuiChip-label': { px: 1 },
                          boxShadow: 'none'
                        }}
                      />
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {!showLoadingState && !hasWidgets && (
        <Box
          sx={{
            borderRadius: 3,
            border: '1px dashed rgba(87, 87, 176, 0.25)',
            backgroundColor: 'rgba(87, 87, 176, 0.04)',
            p: { xs: 2, sm: 3 },
            textAlign: 'center'
          }}
        >
          {isAwaitingWidgets && (
            <CircularProgress size={24} sx={{ color: primaryColor, mb: 1 }} />
          )}
          <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700, mb: 1 }}>
            {emptyStateTitle}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {emptyStateBody}
          </Typography>
          {hasError && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="small"
                onClick={onContinue}
                disabled={isGeneratingWidgets}
                sx={{
                  background: `linear-gradient(to right, ${primaryColor}, ${hoverColor})`,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    background: `linear-gradient(to right, ${hoverColor}, ${primaryColor})`
                  }
                }}
              >
                {t('widget_preview.continue', 'Continue to data sources')}
              </Button>
            </Box>
          )}
          {showNoWidgets && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
              {!isReadOnly && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddWidget}
                  startIcon={<AddIcon />}
                  sx={{
                    borderColor: 'rgba(87, 87, 176, 0.35)',
                    color: primaryColor,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: primaryColor,
                      backgroundColor: 'rgba(87, 87, 176, 0.08)'
                    }
                  }}
                >
                  {t('widget_preview.add', 'Add widget')}
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                onClick={onContinue}
                disabled={isGeneratingWidgets}
                sx={{
                  background: `linear-gradient(to right, ${primaryColor}, ${hoverColor})`,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    background: `linear-gradient(to right, ${hoverColor}, ${primaryColor})`
                  }
                }}
              >
                {t('widget_preview.continue', 'Continue')}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {!showLoadingState && hasWidgets && (
        <>
          <Divider sx={{ my: 3, borderColor: 'rgba(87, 87, 176, 0.12)' }} />
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 0, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={onContinue}
              disabled={isGeneratingWidgets}
              sx={{
                background: `linear-gradient(to right, ${primaryColor}, ${hoverColor})`,
                textTransform: 'none',
                px: 3,
                fontWeight: 600,
                '&:hover': {
                  background: `linear-gradient(to right, ${hoverColor}, ${primaryColor})`
                }
              }}
            >
              {t('widget_preview.continue', 'Continue')}
            </Button>
          </Box>
        </>
      )}

      <AddWidgetModal
        open={widgetModalOpen}
        onClose={handleCloseModal}
        caseid={caseid}
        editingWidget={editingWidget}
      />

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>
          {t('widget_preview.delete_title', 'Delete widget?')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('widget_preview.delete_body', {
              title: widgetToDelete?.title || t('widget_preview.untitled', 'Untitled widget'),
              defaultValue: 'This will remove "{{title}}". You can add it again later.'
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} sx={{ textTransform: 'none' }}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            sx={{ textTransform: 'none' }}
          >
            {deleteLoading ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WidgetPreviewStep;
